import { ReactNode } from "react";
import Chip from "@mui/material/Chip";
import Grid, { GridBaseProps } from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { PricebookBranches } from "../../../../types/price-book.type";
import { VAT_RATE_PERCENT } from "../../../../utils/vat";
import AmountInput from "../../shared/AmountInput";
import { ALL_BRANCHES_LABEL, formatDiscountPercent } from "../constants";
import { selectedBranchCodes } from "./branchSelection";

const CARD_SIZE: GridBaseProps["size"] = { xs: 12, md: 4 };
const DISCOUNT_FIELD_WIDTH = 120;
/** A % keeps 2 decimals, like the API's DECIMAL(5,2). */
const PERCENT_FRACTION_DIGITS = 2;
/** Enough chips to read the scope at a glance; the dialog lists every branch. */
const MAX_BRANCH_CHIPS = 8;

const BOOK_DISCOUNT_HINT = "รายการที่ไม่ได้ตั้ง % เองใช้ค่านี้";
const BOOK_DISCOUNT_ERROR = "0–100";

interface SummaryCardsProps {
  bookDiscountText: string;
  isBookDiscountValid: boolean;
  canEditBookDiscount: boolean;
  onChangeBookDiscount: (text: string) => void;
  branches: PricebookBranches | null;
  branchesLoading: boolean;
  canChangeBranches: boolean;
  onOpenBranchDialog: () => void;
}

/** ส่วนลดทั้งสมุด, สาขาที่ใช้ and ราคาบนบิล — the three facts every line of the book follows. */
export default function SummaryCards({
  bookDiscountText,
  isBookDiscountValid,
  canEditBookDiscount,
  onChangeBookDiscount,
  branches,
  branchesLoading,
  canChangeBranches,
  onOpenBranchDialog,
}: SummaryCardsProps) {
  const branchAction =
    canChangeBranches && branches ? (
      <Link component="button" underline="hover" onClick={onOpenBranchDialog}>
        เปลี่ยน
      </Link>
    ) : null;

  return (
    <Grid container spacing={2}>
      <Grid size={CARD_SIZE}>
        <SummaryCard title="ส่วนลดทั้งสมุด">
          {canEditBookDiscount ? (
            <AmountInput
              value={bookDiscountText}
              maxFractionDigits={PERCENT_FRACTION_DIGITS}
              error={isBookDiscountValid ? undefined : BOOK_DISCOUNT_ERROR}
              ariaLabel="ส่วนลดทั้งสมุด %"
              sx={{ width: DISCOUNT_FIELD_WIDTH }}
              onChange={onChangeBookDiscount}
            />
          ) : (
            <Typography variant="h6" fontWeight={700}>
              {formatDiscountPercent(Number(bookDiscountText))}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {BOOK_DISCOUNT_HINT}
          </Typography>
        </SummaryCard>
      </Grid>
      <Grid size={CARD_SIZE}>
        <SummaryCard title="สาขาที่ใช้" action={branchAction}>
          <BranchChips branches={branches} loading={branchesLoading} />
        </SummaryCard>
      </Grid>
      <Grid size={CARD_SIZE}>
        <SummaryCard title="ราคาบนบิล">
          <Typography variant="body2">
            ร้าน IN: ใช้ราคาสุทธิรวม VAT
          </Typography>
          <Typography variant="body2">
            ร้าน EX: ใช้ราคาสุทธิก่อน VAT แล้วบวก VAT {VAT_RATE_PERCENT}%
          </Typography>
        </SummaryCard>
      </Grid>
    </Grid>
  );
}

interface SummaryCardProps {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}

function SummaryCard({ title, action, children }: SummaryCardProps) {
  return (
    <Paper sx={{ height: "100%" }}>
      <Stack gap={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" fontWeight={600}>
            {title}
          </Typography>
          {action}
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
}

interface BranchChipsProps {
  branches: PricebookBranches | null;
  loading: boolean;
}

function BranchChips({ branches, loading }: BranchChipsProps) {
  if (loading || !branches) {
    const statusText = loading ? "กำลังโหลดสาขา..." : "โหลดสาขาไม่สำเร็จ";
    return (
      <Typography variant="body2" color="text.secondary">
        {statusText}
      </Typography>
    );
  }
  const isAllBranches = branches.branch_scope === "all" && !branches.is_draft;
  const codes = isAllBranches ? [ALL_BRANCHES_LABEL] : selectedBranchCodes(branches);
  const hiddenCount = codes.length - MAX_BRANCH_CHIPS;

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {codes.slice(0, MAX_BRANCH_CHIPS).map((code) => (
        <Chip key={code} label={code} size="small" variant="outlined" />
      ))}
      {hiddenCount > 0 && (
        <Chip label={`+${hiddenCount}`} size="small" variant="outlined" />
      )}
    </Stack>
  );
}
